import type { FaqCategory, FaqCategoryRaw } from "./faqTypes";

/**
 * Validates that a value is a non-empty string.
 * Throws when the value is missing or not a string.
 */
function assertNonEmptyString(value: unknown, fieldPath: string): asserts value is string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`Invalid FAQ content: ${fieldPath} must be a non-empty string`);
  }
}

/**
 * Validates that a value is a non-empty array.
 * Throws when the value is missing or not an array with at least one element.
 */
function assertNonEmptyArray<T>(value: unknown, fieldPath: string): asserts value is T[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error(`Invalid FAQ content: ${fieldPath} must be a non-empty array`);
  }
}

/**
 * Parses and validates raw FAQ category data from next-intl messages.
 * Fails fast with descriptive errors when the shape is invalid.
 */
export function parseFaqCategories(raw: unknown): FaqCategory[] {
  assertNonEmptyArray<FaqCategoryRaw>(raw, "faq.categories");

  return raw.map((category, categoryIndex) => {
    const categoryPath = `faq.categories[${categoryIndex}]`;

    if (typeof category !== "object" || category === null) {
      throw new Error(`Invalid FAQ content: ${categoryPath} must be an object`);
    }

    assertNonEmptyString(category.id, `${categoryPath}.id`);
    assertNonEmptyString(category.label, `${categoryPath}.label`);
    assertNonEmptyString(category.description, `${categoryPath}.description`);
    assertNonEmptyArray(category.sections, `${categoryPath}.sections`);

    const sections = category.sections.map((section, sectionIndex) => {
      const sectionPath = `${categoryPath}.sections[${sectionIndex}]`;

      if (typeof section !== "object" || section === null) {
        throw new Error(`Invalid FAQ content: ${sectionPath} must be an object`);
      }

      assertNonEmptyString(section.id, `${sectionPath}.id`);
      assertNonEmptyString(section.title, `${sectionPath}.title`);
      assertNonEmptyArray(section.items, `${sectionPath}.items`);

      const items = section.items.map((item, itemIndex) => {
        const itemPath = `${sectionPath}.items[${itemIndex}]`;

        if (typeof item !== "object" || item === null) {
          throw new Error(`Invalid FAQ content: ${itemPath} must be an object`);
        }

        assertNonEmptyString(item.question, `${itemPath}.question`);
        assertNonEmptyString(item.answer, `${itemPath}.answer`);

        return {
          question: item.question,
          answer: item.answer,
        };
      });

      return {
        id: section.id,
        title: section.title,
        items,
      };
    });

    return {
      id: category.id,
      label: category.label,
      description: category.description,
      sections,
    };
  });
}

/**
 * Finds a single FAQ category by id from parsed categories.
 * Returns undefined when no matching category exists.
 */
export function findFaqCategoryById(
  categories: FaqCategory[],
  categoryId: string,
): FaqCategory | undefined {
  return categories.find((category) => category.id === categoryId);
}
